import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import BreadCrumbComp from "../../components/breadCrumb";
import SimpleTable from "../../components/tables/simpleTable";
import { icons } from "../../constants";
import { Grid, useMediaQuery } from "@mui/material";
import LaddaButton from "../../components/buttons/laddaButton";
import ModalComponent from "../../components/modals";
import ToastNotification from "../../components/toastNotification";
import VerificationModal from "../../components/modals/verificationModal";
import { useForm } from "react-hook-form";
import {
  callApiAsync,
  createInterestedPartiesComment,
  createUpdateStakeholdersByProcess,
  deleteInterestedParty,
  getInterestedPartiesByProcess,
  getInterestedPartiesCommentsByProcess,
  getLastUpdatedInterestedParty
} from "../../services/apiService";
import {
  interested_parties_enter,
  stakeholdersTabType
} from "../../constants/enums";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  canManageInterestedPartiesProcessRecords,
  canViewInterestedPartiesProcessHistory
} from "../../utils/interestedPartiesPermissions";
import SimpleSelect from "../../components/selects/simpleSelect";
import { useNavigate } from "react-router-dom";
import {
  months,
  stakeholderlevel,
  stakeholderRelationselect,
  stakeholdersEntries,
  stakeholdersinfluence,
  stakeholdersRolType
} from "../../constants/selectData";
import {
  formatCustomDateName,
  formatCustomDateNameLocal,
  formatCustomDateNameWithHours,
  getMonthName
} from "../../utils/dateUtils";
import FormCalendar from "../../components/calendar/formCalendar";
import FormTextField from "../../components/textField/formTextField";
import CharacterCounter from "../../components/characterCounter";
import { StyledTooltip } from "../../components/toolltipComponent";
import InterestedPartiesUpdateHistoryModal from "./InterestedPartiesUpdateHistoryModal";
import "./styles.css";

const STAKEHOLDER_DETAIL_TEXT_TRUNCATE = 150;

const truncateTableCellPreview = (value, max = STAKEHOLDER_DETAIL_TEXT_TRUNCATE) => {
  const s = value == null ? "" : String(value);
  return s.length > max ? `${s.slice(0, max)}...` : s;
};

const validationSchema = yup.object().shape({
  name: yup.string().required("El nombre es obligatorio"),
  requirement: yup.string().required("La necesidad es obligatoria"),
  expectation: yup.string().required("La expectativa es obligatoria"),
  commentDate: yup.date().required("La fecha es obligatoria"),
  comment: yup
    .string()
    .required("El comentario es obligatorio")
    .max(500, "Máximo 500 caracteres")
});

const InterestedPartiesProcessDetail = () => {
  const location = useLocation();
  const { rowData } = location.state || {};

  const [open, setOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterApplied, setFilterApplied] = useState(false);
  const [tableCommentData, setTableCommentData] = useState([]);
  const [dataFilteredTable, setFilteredDataTable] = useState([]);
  const [openVerification, setOpenVerification] = useState(false);
  const [openDeleteVerification, setOpenDeleteVerification] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [yearFilter, setYearFilter] = useState([]);
  const [updateDate, setUpdateDate] = useState(null);
  const [openAddComment, setOpenAddComment] = useState(false);
  const [comment, setComment] = useState("");
  const [date, setDate] = useState(new Date());
  const [toastConfig, setToastConfig] = useState({
    severity: "success",
    summary: "",
    detail: ""
  });
  const [filterCommentApplied, setFilterCommentApplied] = useState(false);
  const [openUpdateHistory, setOpenUpdateHistory] = useState(false);

  const isEditMode = Boolean(selectedRow?.id);
  const isSmallScreen = useMediaQuery("(max-width:590px)");

  const userData = useSelector((state) => state.userData.userData);
  const navigate = useNavigate();
  const canManageProcess = canManageInterestedPartiesProcessRecords(
    userData?.roleList,
    rowData?.value
  );
  const canViewHistory = canViewInterestedPartiesProcessHistory(
    userData?.roleList
  );

  const columnsInterestedPartiesProcessDetail = [
    {
      field: "enter",
      header: "Entrada",
      columnType: "custom-frozen",
      borderRadius: "left",
      render: (rowData) => (
        <p className="table-default-column">
          {(rowData.enter === 1 && interested_parties_enter[1]) ||
            (rowData.enter === 2 && interested_parties_enter[2])}
        </p>
      )
    },
    {
      field: "interested_parties",
      header: "Partes interesadas",
      columnType: "custom-frozen",
      columnWidth: "180px",
      render: (rowData) => {
        const full = rowData.interested_parties ?? "";
        const short = truncateTableCellPreview(full);
        return (
          <StyledTooltip
            title={
              <div>
                <p className="tooltip-text" style={{ whiteSpace: "pre-line" }}>
                  {full || "—"}
                </p>
              </div>
            }
            followCursor
          >
            <div style={{ maxWidth: "180px", overflow: "hidden" }}>
              <p
                className="table-default-column"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  margin: 0
                }}
              >
                {short || "—"}
              </p>
            </div>
          </StyledTooltip>
        );
      }
    },
    {
      field: "rol_type",
      header: (
        <StyledTooltip
          title={
            <>
              <p className="tooltip_info_title">Tipo de Rol</p>
              <div className="VContainer">
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Decisor:</span> Tiene
                  autoridad para aprobar o rechazar decisiones clave.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Financiador:</span> Aporta
                  recursos económicos o materiales.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Ejecutor:</span> Participa
                  directamente en la implementación de actividades.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Usuario final:</span>{" "}
                  Recibe o utiliza el producto/ servicio del proyecto.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Regulador:</span> Supervisa
                  el cumplimiento de normas y leyes. 
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Influenciador:</span> Puede
                  moldear opiniones o decisiones sin tener poder formal.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Observador:</span> No
                  interviene directamente, pero está atento al desarrollo del
                  proyecto. 
                </p>
              </div>
            </>
          }
          placement="top"
        >
          <div className="HContainer pointer">
            <span className="no-wrap mr-10 ">Tipo de rol</span>
            <img
              src={icons.infoExclamationWhite}
              alt="info"
              className="btn-icon-table"
            />
          </div>
        </StyledTooltip>
      ),
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {stakeholdersRolType.find((opt) => opt.value === rowData.rol_type)
            ?.name || "-"}
        </p>
      )
    },
    {
      field: "relation_type",
      header: (
        <StyledTooltip
          title={
            <>
              <p className="tooltip_info_title">Tipo de relación</p>
              <div className="VContainer">
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Directa:</span> El
                  proveedor que entrega materiales esenciales para la ejecución.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Indirecta:</span> La
                  comunidad que se beneficia del impacto ambiental positivo del
                  proyecto.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Formal:</span> Contratos,
                  convenios, acuerdos legales.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Informal:</span> Influencia
                  por reputación, redes sociales o vínculos personales.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Permanente:</span>Empleados
                  de planta o socios estratégicos.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Temporal:</span>{" "}
                  Consultores, contratistas o voluntarios por proyecto.
                </p>
              </div>
            </>
          }
          placement="top"
        >
          <div className="HContainer pointer">
            <span className="no-wrap mr-10 ">Tipo de relacion</span>
            <img
              src={icons.infoExclamationWhite}
              alt="info"
              className="btn-icon-table"
            />
          </div>
        </StyledTooltip>
      ),
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {stakeholderRelationselect.find(
            (opt) => opt.value === rowData.relation_type
          )?.name || "-"}
        </p>
      )
    },
    {
      field: "influence_type",
      header: (
        <StyledTooltip
          title={
            <>
              <p className="tooltip_info_title">Tipo de influencia</p>
              <div className="VContainer">
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Esenciales:</span> Alta
                  influencia en la organización.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Importantes:</span>{" "}
                  Influencia media en la organización.
                </p>
                <p className="tooltip_text">
                  <span className="tooltip_subtitle">Cooperadores:</span> Baja
                  influencia en la organización.
                </p>
              </div>
            </>
          }
          placement="top"
        >
          <div className="HContainer pointer">
            <span className="no-wrap mr-10 ">Tipo de influencia</span>
            <img
              src={icons.infoExclamationWhite}
              alt="info"
              className="btn-icon-table"
            />
          </div>
        </StyledTooltip>
      ),
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {stakeholdersinfluence.find(
            (opt) => opt.value === rowData.influence_type
          )?.name || "-"}
        </p>
      )
    },
    {
      field: "influence_level",
      header: "Nivel de influencia",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {stakeholderlevel.find(
            (opt) => opt.value === rowData.influence_level
          )?.name || "-"}
        </p>
      )
    },
    {
      field: "requirement",
      header: "Necesidad",
      columnType: "tooltip",
      columnWidth: "180px",
      columnMaxWidth: "180px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    {
      field: "expectative",
      header: "Expectativa",
      columnType: "tooltip",
      columnWidth: "180px",
      columnMaxWidth: "180px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    {
      field: "communication_mechanism",
      header: "Mecanismo de comunicación",
      columnType: "tooltip",
      columnWidth: "200px",
      columnMaxWidth: "200px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    {
      field: "accountability",
      header: "Rendición de cuenta",
      columnType: "tooltip",
      columnWidth: "200px",
      columnMaxWidth: "200px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    {
      field: "monitoring_sources",
      header: "Fuentes de monitoreo",
      columnType: "tooltip",
      columnWidth: "200px",
      columnMaxWidth: "200px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    {
      field: "follow_up_comment",
      header: "Comentario de seguimiento",
      columnType: "tooltip",
      columnWidth: "200px",
      columnMaxWidth: "200px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    {
      field: "follow_up_date",
      header: "Fecha de seguimiento",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {rowData.follow_up_date
            ? (rowData.follow_up_date)
            : "—"}
        </p>
      )
    },
    {
      field: "legal_requirement",
      header: "Requisito legal",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {(rowData.legal_requirement === 0 && "No") ||
            (rowData.legal_requirement === 1 && "Sí")}
        </p>
      )
    },
    {
      field: "organization_requirement",
      header: "Requisito de la organización",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {(rowData.organization_requirement === 0 && "No") ||
            (rowData.organization_requirement === 1 && "Sí")}
        </p>
      )
    },
    {
      field: "customer_requirement",
      header: "Requisito del cliente",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {(rowData.customer_requirement === 0 && "No") ||
            (rowData.customer_requirement === 1 && "Sí")}
        </p>
      )
    },
    {
      field: "frequency",
      header: "Frecuencia de monitoreo",
      columnType: "tooltip",
      columnWidth: "160px",
      columnMaxWidth: "160px",
      truncateLength: STAKEHOLDER_DETAIL_TEXT_TRUNCATE
    },
    { field: "actions", header: "", columnType: "actionButtons" }
  ];

  const commentColumns = [
    {
      field: "date",
      header: "Mes/Año/Hora",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {formatCustomDateNameWithHours(rowData.date)}
        </p>
      ),
      columnWidth: "250px"
    },
    { field: "comment", header: "Comentario", columnWidth: "550px" }
  ];

  const {
    control,
    getValues,
    reset,
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const openModal = (row = null) => {
    if (row) {
      reset({
        name: row.name ?? "",
        requirement: row.requirement ?? "",
        expectation: row.expectation ?? ""
      });
      setSelectedRow(row);
    } else {
      reset();
      setSelectedRow(null);
    }
    setOpen(true);
    getCommentsByProcess();
  };

  const getLastUpdate = async () => {
    await callApiAsync(
      () => getLastUpdatedInterestedParty(rowData?.value),
      (data) => {
        setUpdateDate(data.last_update_date);
      }
    );
  };

  const getCommentsByProcess = async () => {
    await callApiAsync(
      () =>
        getInterestedPartiesCommentsByProcess(
          rowData?.value,
          selectedEvent?.id
        ),
      (data) => {
        const sorted = [...data].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setTableCommentData(sorted);

        const years = Array.from(
          new Set(sorted.map((item) => new Date(item.date).getFullYear()))
        )
          .sort((a, b) => b - a)
          .map((year) => ({ name: String(year), value: year }));

        setYearFilter(years);
      }
    );
  };

  const getTableData = async () => {
    await callApiAsync(
      () => getInterestedPartiesByProcess(rowData?.value),
      (data) => {
        setTableData(data);
      }
    );
  };

  const deleteInterestedParties = async () => {
    if (!canManageProcess) {
      return;
    }
    await callApiAsync(
      () =>
        deleteInterestedParty({
          id: selectedRow.id,
          responsible_user_id: userData.id
        }),
      () => {
        setToastConfig({
          severity: "success",
          summary: "Eliminado",
          detail: "Parte interesada eliminada correctamente."
        });
        setShowToast(true);
        getTableData();
      }
    );
    setOpenDeleteVerification(false);
    closeModal();
    setOpen(false);
  };

  const saveCreateUpdate = async (formData) => {
    if (!canManageProcess) {
      return;
    }
    const dataToSend = {
      ...formData,
      id: selectedRow?.id ?? 0,
      process_id: rowData?.value,
      user_creator_id: userData.id,
      type: stakeholdersTabType.byProcess,
      interested_parties_id: selectedEvent?.id
    };

    await callApiAsync(
      () => createUpdateStakeholdersByProcess(dataToSend),
      () => {
        setToastConfig({
          severity: "success",
          summary: "Acción realizada",
          detail: isEditMode
            ? "Se editó la parte interesada correctamente."
            : "Se creó la parte interesada con éxito."
        });
        setShowToast(true);
        getTableData();
      }
    );
    closeModal();
  };

  const closeModal = () => {
    reset();
    setSelectedRow(null);
    setSelectedEvent(null);
    handleCloseVerification(false);
    setOpen(false);
  };

  const handleClearFilters = () => {
    reset({
      monthFilter: null,
      yearFilter: null,
      comment: getValues("comment") ?? ""
    });
    setFilteredTableData([]);
    setFilterCommentApplied(false);
  };

  const handleFilter = () => {
    const { monthFilter, yearFilter: yearFilterField } = getValues();

    const month = monthFilter
      ? typeof monthFilter === "object"
        ? Number(monthFilter.value)
        : Number(monthFilter)
      : null;

    const year = yearFilterField
      ? typeof yearFilterField === "object"
        ? Number(yearFilterField.value)
        : Number(yearFilterField)
      : null;

    const filtered = tableCommentData.filter((item) => {
      const d = new Date(item.date);
      const itemMonth = d.getMonth() + 1;
      const itemYear = d.getFullYear();

      const matchMonth = month ? itemMonth === month : true;
      const matchYear = year ? itemYear === year : true;

      return matchMonth && matchYear;
    });
    setFilteredTableData(filtered);
    setFilterCommentApplied(true);
  };

  const parseOpt = (opt) => {
    if (opt === null || opt === undefined || opt === "") return null;
    return typeof opt === "object" ? opt.value ?? null : Number(opt);
  };

  const handleFilterMain = () => {
    const { enter, rol_type, relation_type, influence_type, influence_level } =
      getValues();

    const enterVal = parseOpt(enter);
    const rolTypeVal = parseOpt(rol_type);
    const relationTypeVal = parseOpt(relation_type);
    const influenceTypeVal = parseOpt(influence_type);
    const influenceLevelVal = parseOpt(influence_level);

    const isEmptyFilter =
      enterVal === null &&
      rolTypeVal === null &&
      relationTypeVal === null &&
      influenceTypeVal === null &&
      influenceLevelVal === null;

    if (isEmptyFilter) {
      setFilteredDataTable([]);
      setFilterApplied(false);
      return;
    }

      const filtered = tableData.filter((row) => {
      const matchEnter = enterVal !== null ? row.enter === enterVal : true;
      const matchRolType =
        rolTypeVal !== null ? row.rol_type === rolTypeVal : true;
      const matchRelationType =
        relationTypeVal !== null ? row.relation_type === relationTypeVal : true;
      const matchInfluence =
        influenceTypeVal !== null
          ? row.influence_type === influenceTypeVal
          : true;
      const matchLevel =
        influenceLevelVal !== null
          ? row.influence_level === influenceLevelVal
          : true;
      return (
        matchEnter &&
        matchRolType &&
        matchRelationType &&
        matchInfluence &&
        matchLevel
      );
    });

    setFilteredDataTable(filtered);
    setFilterApplied(true);
  };

  const handleClearMain = () => {
    reset({
      enter: null,
      rol_type: null,
      relation_type: null,
      influence_type: null,
      influence_level: null,

      monthFilter: getValues("monthFilter") ?? null,
      yearFilter: getValues("yearFilter") ?? null,
      comment: getValues("comment") ?? ""
    });
    setFilterApplied(false);
    setFilteredDataTable([]);
  };

  const onSubmitAddComment = async () => {
    if (!canManageProcess) {
      return;
    }
    const values = getValues();

    const dataToSend = {
      comment: values.comment,
      process_id: rowData?.value,
      user_creator_id: userData?.id,
      interested_parties_id: selectedEvent?.id
    };

    await callApiAsync(
      () => createInterestedPartiesComment(dataToSend),
      () => {
        setToastConfig({
          severity: "success",
          summary: "Comentario agregado",
          detail: "El comentario fue registrado correctamente."
        });
        setShowToast(true);
        getCommentsByProcess();
        setOpenAddComment(false);
        reset({ comment: "" });
      }
    );
  };

  const handleOpenVerification = () => setOpenVerification(true);
  const handleCloseVerification = () => setOpenVerification(false);

  const handleOpenDeleteVerification = (row) => {
    setSelectedRow(row);
    setOpenDeleteVerification(true);
  };
  const handleCloseDeleteVerification = () => setOpenDeleteVerification(false);

  useEffect(() => {
    getTableData();
    getLastUpdate();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      getCommentsByProcess();
    }
  }, [selectedEvent]);

  const ToolTipDescription = (text) => {
    return (
      <div>
        <p className="tooltip-text" style={{ whiteSpace: "pre-line" }}>
          {text}
        </p>
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}
      >
        <div>
          <BreadCrumbComp
            personalized={`Partes Interesadas - ${rowData?.name}`}
          />
          <h1 className="gm-header-title-grey mb-25">
            PARTES INTERESADAS - {rowData?.name}
          </h1>
        </div>

        {(canViewHistory || canManageProcess) && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end"
          }}
        >
          {canViewHistory && (
            <LaddaButton
              variant="contained"
              width={"260px"}
              height={"40px"}
              backgroundColor="#f0cd01"
              borderColor="#f0cd01"
              color="#333333"
              onClick={() => setOpenUpdateHistory(true)}
            >
              <p className="button_text">Control de actualización</p>
            </LaddaButton>
          )}
          {canManageProcess && (
            <LaddaButton
              variant="contained"
              width={"270px"}
              height={"40px"}
              onClick={() =>
                navigate(
                  "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail/create",
                  {
                    state: { rowData: rowData, loadData: rowData }
                  }
                )
              }
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  className="btn-icon"
                  src={icons.plusIcon}
                  alt="icon"
                  style={{ marginRight: "10px" }}
                />
                <p className="button_text">{`Crear partes interesada`}</p>
              </div>
            </LaddaButton>
          )}
        </div>
        )}
      </div>
      <form
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "10px"
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleSelect
              name="enter"
              control={control}
              label="Entrada"
              options={stakeholdersEntries}
              margin="0"
              searchBar={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleSelect
              name="rol_type"
              control={control}
              label="Tipo de rol"
              options={stakeholdersRolType}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleSelect
              name="relation_type"
              control={control}
              label="Tipo de relacion"
              options={stakeholderRelationselect}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleSelect
              name="influence_type"
              control={control}
              label="Tipo de influencia"
              options={stakeholdersinfluence}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleSelect
              name="influence_level"
              control={control}
              label="Nivel de influencia"
              options={stakeholderlevel}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <div className="IHContainer j-end">
              <LaddaButton
                variant="contained"
                onClick={handleFilterMain}
                margin={"0"}
              >
                <img className="btn-icon" src={icons.filterWhite} alt="icon" />
              </LaddaButton>

              <LaddaButton
                variant="contained"
                onClick={handleClearMain}
                margin={"0 0 0 10px"}
              >
                <img
                  className="btn-icon"
                  src={icons.clearFilterWhite}
                  alt="icon"
                />
              </LaddaButton>
            </div>
          </Grid>
        </Grid>
      </form>

      <SimpleTable
        data={filterApplied ? dataFilteredTable : tableData}
        columns={columnsInterestedPartiesProcessDetail}
        tooltipFragment={ToolTipDescription}
        headerElement={
          <div className="j-end mr-10">
            <div className="j-space-between">
              <p className="lastC-text">Última edición:</p>
              <p className="li-item">
                {updateDate
                  ? formatCustomDateName(updateDate)
                  : "Sin actualización"}
              </p>
            </div>
          </div>
        }
        buttons={[
          ...(canViewHistory
            ? [
                {
                  type: "icon",
                  icon: icons.so_comment,
                  onClick: (data) => {
                    setSelectedEvent(data);
                    openModal(data);
                  }
                }
              ]
            : []),
          {
            type: "icon",
            icon: canManageProcess ? icons.editYellow : icons.eye,
            onClick: (data) => {
              if (canManageProcess) {
                navigate(
                  "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail/create",
                  {
                    state: { rowData: data, loadData: rowData, mode: "edit" }
                  }
                );
              } else {
                navigate(
                  "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail/create",
                  {
                    state: { rowData: data, loadData: rowData, mode: "view" }
                  }
                );
              }
            }
          },
          ...(canManageProcess
            ? [
                {
                  type: "icon",
                  icon: icons.redTrash,
                  onClick: (data) => {
                    handleOpenDeleteVerification(data);
                  }
                }
              ]
            : [])
        ]}
        isPaginated={true}
      />

      <InterestedPartiesUpdateHistoryModal
        open={openUpdateHistory}
        onClose={() => setOpenUpdateHistory(false)}
        processId={rowData?.value}
        parties={tableData}
        processRowData={rowData}
        canEditFromHistory={canManageProcess}
        notify={(cfg) => {
          setToastConfig(cfg);
          setShowToast(true);
        }}
      />

      <ModalComponent
        open={open}
        handleClose={closeModal}
        title={"Historial Comentarios"}
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <div className="filter-container">
          <form>
            <Grid justifyContent="flex-end" container spacing={1}>
              <Grid item xs={12} md={4}>
                <div className="mid-filter-margin">
                  <SimpleSelect
                    name="monthFilter"
                    control={control}
                    label="Mes"
                    options={months}
                    margin={"0"}
                  />
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <SimpleSelect
                  name="yearFilter"
                  control={control}
                  label="Año"
                  options={yearFilter}
                  margin={"0"}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <div className="IHContainer j-end">
                  <LaddaButton
                    variant="contained"
                    onClick={handleFilter}
                    margin={"0"}
                    width={"52px"}
                  >
                    <img
                      className="btn-icon"
                      src={icons.filterWhite}
                      alt="icon"
                    />
                  </LaddaButton>
                  <LaddaButton
                    variant="contained"
                    onClick={handleClearFilters}
                    margin={"0 -1px 0 5px"}
                    width={"52px"}
                  >
                    <img
                      className="btn-icon"
                      src={icons.clearFilterWhite}
                      alt="icon"
                    />
                  </LaddaButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
        <SimpleTable
          data={filterCommentApplied ? filteredTableData : tableCommentData}
          frozen={isSmallScreen ? false : true}
          columns={commentColumns}
        />
        {canManageProcess && (
          <div className="HContainer j-center w-complete">
            <LaddaButton
              variant="contained"
              onClick={() => setOpenAddComment(true)}
            >
              <div className="HContainer align-center">
                <p className="button_text">Agregar</p>
              </div>
            </LaddaButton>
          </div>
        )}
      </ModalComponent>

      <ModalComponent
        open={openAddComment}
        handleClose={() => setOpenAddComment(false)}
        title="Nuevo Comentario"
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <form>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <FormCalendar
                name="commentDate"
                control={control}
                formLabel="Fecha"
                defaultValue={new Date()}
                margin="0"
                disabled
              />
            </Grid>

            {/* TextArea */}
            <Grid item xs={12}>
              <div>
                <FormTextField
                  name={"comment"}
                  control={control}
                  label="Comentario:"
                  isTextArea={true}
                  error={errors.comment}
                  maxLength={500}
                  rows={3}
                  margin={"0"}
                  labelColor={"#333333"}
                  disabled={!canManageProcess}
                />
                <CharacterCounter
                  name={"comment"}
                  control={control}
                  maxLength={500}
                />
              </div>
            </Grid>

            {canManageProcess && (
            <Grid
              item
              xs={12}
              className="HContainer j-center w-complete mt-25"
            >
              <LaddaButton
                variant="contained"
                onClick={() => onSubmitAddComment()}
              >
                Guardar
              </LaddaButton>
            </Grid>
            )}
          </Grid>
        </form>
      </ModalComponent>

      <VerificationModal
        open={openVerification}
        handleClose={handleCloseVerification}
        alertText={
          <>
            <b>
              {isEditMode
                ? "¿Estás seguro de guardar los cambios de la parte interesada?"
                : "¿Estás seguro de crear la nueva parte interesada?"}
            </b>
          </>
        }
        onConfirm={handleSubmit(saveCreateUpdate)}
      />

      <VerificationModal
        open={openDeleteVerification}
        handleClose={handleCloseDeleteVerification}
        alertText={
          <>
            <b>¿Estás seguro de querer eliminar esta parte interesada?</b>
          </>
        }
        onConfirm={deleteInterestedParties}
      />
      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
        position="top-right"
      />
    </div>
  );
};

export default InterestedPartiesProcessDetail;
